import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowUp, ArrowDown, ChevronsUpDown, X, ExternalLink, Car, Percent, Calendar } from 'lucide-react'
import LoadingSpinner from '../../components/LoadingSpinner'
import ErrorMessage from '../../components/ErrorMessage'
import SearchBar from '../../components/SearchBar'
import ExportButtons from '../../components/ExportButtons'
import CellTooltip from '../../components/CellTooltip'
import { ColumnDefinition } from '../../utils/exportToCsv'
import { formatNorwegianDate } from '../../utils/dateUtils'
import { fetchLoanCampaigns, LoanCampaign } from './loanCampaignsApi'
import ForbeholdPanel from './ForbeholdPanel'
import SummaryCards from './SummaryCards'
import i18n from '../../i18n'
import { localizeApiDetail } from '../../i18n/apiError'

const MISSING = '–'

/** The bank filter's name for a code whose raw names are brand-specific.
 *  VWF rows say Audi, CUPRA, Škoda or Volkswagen Financial Services, and the
 *  rows arrive sorted by brand, so Audi named the whole code (#671). */
const BANK_LABELS: Record<string, string> = {
  VWF: 'Volkswagen Financial Services',
}

/**
 * "Datagrunnlag og forbehold" (#498), verbatim from the issue.
 *
 * One source, two consumers: the panel renders a <p> per paragraph and the
 * PDF export passes the joined string as ExportMetadata.explanationText.
 * CSV and Excel read no such field — that split is deliberate, a prose block
 * in a data file breaks anyone parsing it.
 *
 * Loan only. The wording is about lånevilkår and finansieringsselskaper; the
 * leasing tab has its own text (#636). Both render through ForbeholdPanel.
 *
 * The text lives in kampanjer:loan.forbehold (#714) and is read at render.
 */

/** null means "the source did not publish this". 0 means zero — 21 of 127 rows
 *  carry a real 0 % rate and 46 a real 0 kr down payment, so these helpers test
 *  `== null` and never falsiness. */
const num = (v: string | null | undefined): number | null =>
  v == null || v === '' ? null : Number(v)

const fmtKr = (v: string | null | undefined): string => {
  const n = num(v)
  if (n == null || Number.isNaN(n)) return MISSING
  return `${n.toLocaleString('nb-NO', { maximumFractionDigits: 0 })} kr`
}

/** The API sends percent units: "0.990" is 0.99 %. Do not rescale. */
const fmtPct = (v: string | null | undefined): string => {
  const n = num(v)
  if (n == null || Number.isNaN(n)) return MISSING
  return `${n.toLocaleString('nb-NO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %`
}

const fmtMnd = (v: number | null | undefined): string =>
  v == null ? MISSING : i18n.t('loan.months', { ns: 'kampanjer', n: v })

// Grid variants: bare numbers, no unit suffix. Same convention as the leasing
// grid, where the unit lives in the column header's title and the cell holds
// only the figure — 13 columns of "709 900 kr" and "0,99 %" wrap onto two lines
// each and push the row height up for no information gained.
const gridKr = (v: string | null | undefined): string => {
  const n = num(v)
  if (n == null || Number.isNaN(n)) return MISSING
  return n.toLocaleString('nb-NO', { maximumFractionDigits: 0 })
}

const gridPct = (v: string | null | undefined): string => {
  const n = num(v)
  if (n == null || Number.isNaN(n)) return MISSING
  return n.toLocaleString('nb-NO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const gridMnd = (v: number | null | undefined): string =>
  v == null ? MISSING : String(v)

const fmtDato = (v: string | null | undefined): string =>
  v == null ? MISSING : formatNorwegianDate(v)

const fmtText = (v: string | null | undefined): string =>
  v == null || v === '' ? MISSING : v

/**
 * Cell contents as a link out to the source, when there is one.
 *
 * Only Modell carries a link (#497 AC1). Bilpris used to link to KildeUrl as
 * well, but two links per row read as two different destinations when on 123
 * of 127 rows they are the same page — the source is still one click away
 * from Modell, and from both URLs in the detail modal.
 */
function Kilde({ href, children }: { href: string | null; children: React.ReactNode }) {
  if (!href) return <>{children}</>
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary-600 dark:text-primary-400 hover:underline"
    >
      {children}
    </a>
  )
}

/** Ascending compare with null last — shared by the default sort and its
 *  tiebreaker so both treat a missing rate the same way. */
const compareNullsLast = (a: number | null, b: number | null): number => {
  if (a == null && b == null) return 0
  if (a == null) return 1
  if (b == null) return -1
  return a - b
}

// P1/P2 is the spec's vocabulary (bilpriser-backend#490): P1 is the campaign
// period (the rate fixing), P2 the remainder of the term on the product rate.
// 100 of 130 rows have P1 shorter than the term and 98 have a higher rate in
// P2, so both periods belong in the grid, not behind the detail button.
// The explanation is kampanjer:loan.p1p2 (#714).

type SortKey =
  | 'MerkeRaw' | 'Modell' | 'Variant' | 'BankCode'
  | 'NominellRentePct' | 'ProduktrenteEtterKampanjePct' | 'EffektivRentePct'
  | 'RentebindingMnd' | 'LopetidMnd'
  | 'BilprisNOK' | 'EgenkapitalNOK' | 'LanebelopNOK'
  | 'KampanjeTil'

const NUMERIC_KEYS: SortKey[] = [
  'NominellRentePct', 'ProduktrenteEtterKampanjePct', 'EffektivRentePct',
  'RentebindingMnd', 'LopetidMnd',
  'BilprisNOK', 'EgenkapitalNOK', 'LanebelopNOK',
]
// KampanjeTil sorts as a string: the API serialises it as an ISO date, and ISO
// dates order correctly under localeCompare.

export default function LoanCampaignsTab() {
  const { t } = useTranslation('kampanjer')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [rows, setRows] = useState<LoanCampaign[]>([])
  const [kontrollDato, setKontrollDato] = useState<string | null>(null)

  const [searchTerm, setSearchTerm] = useState('')
  const [merke, setMerke] = useState('')
  const [bank, setBank] = useState('')

  const [sortConfig, setSortConfig] = useState<{ key: SortKey; direction: 'asc' | 'desc' } | null>(null)
  const [selected, setSelected] = useState<LoanCampaign | null>(null)

  // One fetch on mount. The batch is ~130 rows and every filter below is
  // client-side, so no effect here can re-fire and stack requests.
  useEffect(() => {
    let cancelled = false
    fetchLoanCampaigns()
      .then((result) => {
        if (cancelled) return
        setRows(result.data)
        setKontrollDato(result.kontroll_dato)
      })
      .catch((err) => {
        if (cancelled) return
        setError(localizeApiDetail(err.response?.data?.detail) || err.message || i18n.t('loan.loadFailed', { ns: 'kampanjer' }))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [])

  const merker = useMemo(
    () => Array.from(new Set(rows.map((r) => r.MerkeRaw))).sort((a, b) => a.localeCompare(b, 'nb-NO')),
    [rows],
  )

  // Kept as the source string so fmtPct formats it like the grid does. A null
  // rate is skipped; a real 0 % is a rate and can be the lowest.
  const lavesteEffektivRente = useMemo(() => {
    let best: string | null = null
    for (const r of rows) {
      const n = num(r.EffektivRentePct)
      if (n == null || Number.isNaN(n)) continue
      if (best == null || n < Number(best)) best = r.EffektivRentePct
    }
    return best
  }, [rows])

  // Label by the raw company name, filter by the normalised code: 18 raw names
  // collapse to 8 codes, and several raw names mention two banks. A code in
  // BANK_LABELS gets that name instead of whichever raw name came first.
  const banker = useMemo(() => {
    const byCode = new Map<string, string>()
    for (const r of rows) {
      if (r.BankCode && !byCode.has(r.BankCode)) {
        byCode.set(r.BankCode, BANK_LABELS[r.BankCode] || r.FinansieringsselskapRaw || r.BankCode)
      }
    }
    return Array.from(byCode.entries()).sort((a, b) => a[1].localeCompare(b[1], 'nb-NO'))
  }, [rows])

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase()
    return rows.filter((r) => {
      if (merke && r.MerkeRaw !== merke) return false
      if (bank && r.BankCode !== bank) return false
      if (!q) return true
      return [r.MerkeRaw, r.Modell, r.Variant, r.FinansieringsselskapRaw]
        .some((field) => (field || '').toLowerCase().includes(q))
    })
  }, [rows, searchTerm, merke, bank])

  const sorted = useMemo(() => {
    const out = [...filtered]
    if (!sortConfig) {
      // Default: cheapest campaign first — P1 nom. % ascending, effective rate
      // as tiebreaker (the spec's sort order). Rows with no rate go last; a
      // genuine 0 % (21 rows) sorts as the number 0, at the top.
      out.sort((a, b) => {
        const cmp = compareNullsLast(num(a.NominellRentePct), num(b.NominellRentePct))
        if (cmp !== 0) return cmp
        return compareNullsLast(num(a.EffektivRentePct), num(b.EffektivRentePct))
      })
      return out
    }
    const { key, direction } = sortConfig
    const numeric = NUMERIC_KEYS.includes(key)
    out.sort((a, b) => {
      const av = numeric ? num(a[key] as string | null) : (a[key] as string | null)
      const bv = numeric ? num(b[key] as string | null) : (b[key] as string | null)
      // Missing values sort last in both directions: a "–" at the top of a
      // descending sort reads as the largest value, which it is not.
      if (av == null && bv == null) return 0
      if (av == null) return 1
      if (bv == null) return -1
      const cmp = numeric
        ? (av as number) - (bv as number)
        : String(av).localeCompare(String(bv), 'nb-NO')
      return direction === 'asc' ? cmp : -cmp
    })
    return out
  }, [filtered, sortConfig])

  const handleSort = (key: SortKey) => {
    if (sortConfig?.key === key) {
      setSortConfig(sortConfig.direction === 'asc' ? { key, direction: 'desc' } : null)
    } else {
      setSortConfig({ key, direction: 'asc' })
    }
  }

  const sortIcon = (key: SortKey) => {
    if (sortConfig?.key !== key) return <ChevronsUpDown className="inline w-3 h-3 ml-1 opacity-40" />
    return sortConfig.direction === 'asc'
      ? <ArrowUp className="inline w-3 h-3 ml-1" />
      : <ArrowDown className="inline w-3 h-3 ml-1" />
  }

  // Grid columns in grid order first, then the detail-only fields (#609 AC6).
  // Units come back here and in the modal — the cells drop them, the exports
  // must not.
  const exportColumns: ColumnDefinition[] = [
    { key: 'MerkeRaw', label: t('loan.col.merke') },
    { key: 'Modell', label: t('loan.col.modell'), format: fmtText },
    { key: 'Variant', label: t('loan.col.variant'), format: fmtText },
    { key: 'BankCode', label: t('loan.col.bank'), format: fmtText },
    { key: 'NominellRentePct', label: t('loan.col.p1Nom'), format: fmtPct },
    { key: 'ProduktrenteEtterKampanjePct', label: t('loan.col.p2Nom'), format: fmtPct },
    { key: 'EffektivRentePct', label: t('loan.col.eff'), format: fmtPct },
    { key: 'RentebindingMnd', label: t('loan.col.p1Md'), format: fmtMnd },
    { key: 'LopetidMnd', label: t('loan.col.p1p2Md'), format: fmtMnd },
    { key: 'BilprisNOK', label: t('loan.col.bilpris'), format: fmtKr },
    { key: 'EgenkapitalNOK', label: t('loan.col.egenkapital'), format: fmtKr },
    { key: 'LanebelopNOK', label: t('loan.col.lanebelop'), format: fmtKr },
    { key: 'KampanjeTil', label: t('loan.col.utlop'), format: fmtDato },
    { key: 'Status', label: t('loan.col.status'), format: fmtText },
    { key: 'FinansieringsselskapRaw', label: t('loan.col.finansieringsselskap'), format: fmtText },
    { key: 'lanetype_p1', label: t('loan.col.lanetypeP1'), format: fmtText },
    { key: 'EgenkapitalPct', label: t('loan.col.egenkapitalPct'), format: fmtPct },
    { key: 'ManedsbelopNOK', label: t('loan.col.manedsbelop'), format: fmtKr },
    { key: 'TotalkostnadNOK', label: t('loan.col.totalkostnad'), format: fmtKr },
    { key: 'EtableringsgebyrNOK', label: t('loan.col.etableringsgebyr'), format: fmtKr },
    { key: 'TermingebyrNOK', label: t('loan.col.termingebyr'), format: fmtKr },
    { key: 'Kampanjenavn', label: t('loan.col.kampanjenavn'), format: fmtText },
    { key: 'KampanjeFra', label: t('loan.col.kampanjeFra'), format: fmtDato },
    { key: 'InkludertUtstyr', label: t('loan.col.inkludertUtstyr'), format: fmtText },
    { key: 'Merknad', label: t('loan.col.merknad'), format: fmtText },
    { key: 'EndringFraForrigeKontroll', label: t('loan.col.endring'), format: fmtText },
    { key: 'KildeUrl', label: t('loan.col.kilde'), format: fmtText },
    { key: 'KampanjeUrl', label: t('loan.col.kampanjeside'), format: fmtText },
  ]

  const forbeholdTittel = t('loan.forbehold.title')
  const forbeholdAvsnitt = t('loan.forbehold.paragraphs', { returnObjects: true }) as string[]

  if (loading) return <LoadingSpinner />
  if (error) return <ErrorMessage message={error} />

  const hasFilters = !!(searchTerm || merke || bank)

  return (
    <div className="space-y-6">
      {/* First thing on the tab, above the heading, and outside the
          empty-state branch (#498 AC1): the forbehold describes the source,
          which is worth saying even on a day when no batch has been uploaded. */}
      <ForbeholdPanel title={forbeholdTittel} paragraphs={forbeholdAvsnitt} testId="loan-forbehold" />

      <div className="flex justify-between items-start gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">{t('loan.title')}</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            {t('loan.subtitle')}
            {kontrollDato && (
              <>
                {' '}
                <span data-testid="loan-kontrolldato">
                  {t('loan.checked', { date: formatNorwegianDate(kontrollDato) })}
                </span>
                .
              </>
            )}
          </p>
        </div>
        <ExportButtons
          data={sorted}
          columns={exportColumns}
          metadata={{
            title: `${t('loan.title')}${kontrollDato ? ` ${kontrollDato}` : ''}`,
            dateRange: kontrollDato ? formatNorwegianDate(kontrollDato) : undefined,
            // PDF only (#498 AC4). exportToPdf is the sole reader of this
            // field; exportToCsv and exportToExcel ignore it, which is what
            // keeps the forbehold out of the data files.
            explanationText: `${forbeholdTittel}. ${forbeholdAvsnitt.join(' ')}`,
            filters: [
              merke ? t('loan.filterMerke', { value: merke }) : '',
              bank ? t('loan.filterBank', { value: bank }) : '',
            ].filter(Boolean),
          }}
        />
      </div>

      {rows.length === 0 ? (
        <div data-testid="loan-empty-state" className="card p-8 text-center text-gray-600 dark:text-gray-400">
          {t('loan.empty')}
        </div>
      ) : (
        <>
          {/* Same three cards as Leasingkampanjer (#631, #632), over the whole
              batch rather than the filtered view. */}
          <SummaryCards
            cards={[
              { icon: Car, tone: 'primary', label: t('cards.totalCampaigns'), value: rows.length },
              { icon: Percent, tone: 'green', label: t('cards.lowestEffectiveRate'), value: fmtPct(lavesteEffektivRente) },
              { icon: Calendar, tone: 'purple', label: t('cards.uniqueBrands'), value: merker.length },
            ]}
          />

          <div className="card p-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="md:col-span-2">
                <SearchBar
                  value={searchTerm}
                  onChange={setSearchTerm}
                  placeholder={t('loan.searchPlaceholder')}
                />
              </div>
              <select
                aria-label={t('loan.col.merke')}
                value={merke}
                onChange={(e) => setMerke(e.target.value)}
                className="input-field w-full"
              >
                <option value="">{t('loan.allMerker')}</option>
                {merker.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
              <select
                aria-label={t('loan.col.finansieringsselskap')}
                value={bank}
                onChange={(e) => setBank(e.target.value)}
                className="input-field w-full"
              >
                <option value="">{t('loan.allBanks')}</option>
                {banker.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
              </select>
            </div>
          </div>

          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('loan.showing', { shown: sorted.length, total: rows.length })}
            </p>
            <div className="flex gap-2">
              {sortConfig && (
                <button onClick={() => setSortConfig(null)} className="btn-secondary text-sm">
                  {t('resetSort')}
                </button>
              )}
              {hasFilters && (
                <button
                  onClick={() => { setSearchTerm(''); setMerke(''); setBank('') }}
                  className="btn-secondary text-sm"
                >
                  {t('resetFilter')}
                </button>
              )}
            </div>
          </div>

          <div className="card overflow-x-auto">
            <table data-testid="loan-campaigns-table" className="data-table data-table-compact">
              <thead>
                <tr>
                  <th className="cursor-pointer" onClick={() => handleSort('MerkeRaw')}>{t('loan.col.merke')} {sortIcon('MerkeRaw')}</th>
                  {/* A little wider than the content demands (#497 AC5): the
                      column sized itself to the shortest model names and left
                      the longer ones wrapping onto two lines. */}
                  <th className="cursor-pointer min-w-36" onClick={() => handleSort('Modell')}>{t('loan.col.modell')} {sortIcon('Modell')}</th>
                  <th className="cursor-pointer" onClick={() => handleSort('Variant')}>{t('loan.col.variant')} {sortIcon('Variant')}</th>
                  <th className="cursor-pointer" onClick={() => handleSort('BankCode')} title={t('loan.colTitle.bank')}>{t('loan.col.bank')} {sortIcon('BankCode')}</th>
                  {/* Hover texts verbatim from bilpriser-backend#503. */}
                  <th className="text-right cursor-pointer" onClick={() => handleSort('NominellRentePct')} title={t('loan.colTitle.p1Nom')}>{t('loan.col.p1Nom')} {sortIcon('NominellRentePct')}</th>
                  <th className="text-right cursor-pointer" onClick={() => handleSort('ProduktrenteEtterKampanjePct')} title={t('loan.colTitle.p2Nom')}>{t('loan.col.p2Nom')} {sortIcon('ProduktrenteEtterKampanjePct')}</th>
                  <th className="text-right cursor-pointer" onClick={() => handleSort('EffektivRentePct')} title={t('loan.colTitle.eff')}>{t('loan.col.eff')} {sortIcon('EffektivRentePct')}</th>
                  <th className="text-right cursor-pointer" onClick={() => handleSort('RentebindingMnd')} title={t('loan.colTitle.p1Md')}>{t('loan.col.p1Md')} {sortIcon('RentebindingMnd')}</th>
                  <th className="text-right cursor-pointer" onClick={() => handleSort('LopetidMnd')} title={t('loan.colTitle.p1p2Md', { p1p2: t('loan.p1p2') })}>{t('loan.col.p1p2Md')} {sortIcon('LopetidMnd')}</th>
                  <th className="text-right cursor-pointer" onClick={() => handleSort('BilprisNOK')} title={t('loan.colTitle.bilpris')}>{t('loan.col.bilpris')} {sortIcon('BilprisNOK')}</th>
                  <th className="text-right cursor-pointer" onClick={() => handleSort('EgenkapitalNOK')} title={t('loan.colTitle.egenkapital')}>{t('loan.col.egenkapital')} {sortIcon('EgenkapitalNOK')}</th>
                  <th className="text-right cursor-pointer" onClick={() => handleSort('LanebelopNOK')} title={t('loan.colTitle.lanebelop')}>{t('loan.col.lanebelop')} {sortIcon('LanebelopNOK')}</th>
                  <th className="cursor-pointer" onClick={() => handleSort('KampanjeTil')} title={t('loan.colTitle.utlop')}>{t('loan.col.utlop')} {sortIcon('KampanjeTil')}</th>
                  <th className="text-center">{t('action')}</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((r, idx) => (
                  // Zebra striping, same treatment as Nybilstatistikk (#497 AC3).
                  <tr
                    key={r.ResultID}
                    data-testid="loan-campaign-row"
                    className={`hover:bg-gray-100 dark:hover:bg-dark-border transition-colors ${idx % 2 === 0 ? 'bg-gray-50 dark:bg-gray-800/30' : ''}`}
                  >
                    {/* No wrapping (bilpriser-backend#503): the hyphen in
                        "Mercedes-Benz" / "Mercedes-B." is a break opportunity,
                        so the column shrank to "Mercedes-" and wrapped. */}
                    <td className="font-bold whitespace-nowrap text-gray-900 dark:text-gray-100">{r.MerkeRaw}</td>
                    <td>
                      <Kilde href={r.KampanjeUrl}>{fmtText(r.Modell)}</Kilde>
                    </td>
                    {/* Variant is long enough to wrap onto two lines, which
                        pushed the Handling column past the right edge.
                        Truncated with the full value on hover. Narrowed from
                        w-32 to make room for the unwrapped Merke column
                        (bilpriser-backend#503) without a scrollbar at 1440px. */}
                    <td className="text-gray-600 dark:text-gray-400">
                      {r.Variant ? (
                        <CellTooltip content={<span>{r.Variant}</span>}>
                          <span className="block w-24 truncate">{r.Variant}</span>
                        </CellTooltip>
                      ) : MISSING}
                    </td>
                    <td>
                      {/* The code is the cell, the raw company name the
                          tooltip: 18 raw names collapse to 8 codes, and the
                          full names wrapped every row onto two lines. Content
                          is passed as a node, not a string — CellTooltip
                          renders strings with dangerouslySetInnerHTML and this
                          text comes from an uploaded file. */}
                      {r.BankCode ? (
                        <CellTooltip content={<span>{r.FinansieringsselskapRaw || r.BankCode}</span>}>
                          <span>{r.BankCode}</span>
                        </CellTooltip>
                      ) : fmtText(r.FinansieringsselskapRaw)}
                    </td>
                    <td className="text-right font-medium">{gridPct(r.NominellRentePct)}</td>
                    <td className="text-right">{gridPct(r.ProduktrenteEtterKampanjePct)}</td>
                    <td className="text-right">{gridPct(r.EffektivRentePct)}</td>
                    <td className="text-right">{gridMnd(r.RentebindingMnd)}</td>
                    <td className="text-right">{gridMnd(r.LopetidMnd)}</td>
                    <td className="text-right">{gridKr(r.BilprisNOK)}</td>
                    <td className="text-right">{gridKr(r.EgenkapitalNOK)}</td>
                    <td className="text-right">{gridKr(r.LanebelopNOK)}</td>
                    <td>{fmtDato(r.KampanjeTil)}</td>
                    <td className="text-center">
                      <button onClick={() => setSelected(r)} className="text-primary-600 dark:text-primary-400 hover:underline text-sm">
                        {t('details')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {sorted.length === 0 && (
              <p data-testid="loan-no-matches" className="p-6 text-center text-gray-600 dark:text-gray-400">
                {t('loan.noMatches')}
              </p>
            )}
          </div>
        </>
      )}

      {selected && <LoanCampaignModal row={selected} onClose={() => setSelected(null)} />}
    </div>
  )
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div data-label={label} className="flex justify-between gap-4 py-1.5 border-b border-gray-100 dark:border-gray-800 last:border-0">
      <span className="text-sm text-gray-600 dark:text-gray-400">{label}</span>
      <span className="text-sm font-medium text-gray-900 dark:text-gray-100 text-right">{value}</span>
    </div>
  )
}

function LoanCampaignModal({ row, onClose }: { row: LoanCampaign; onClose: () => void }) {
  const { t } = useTranslation('kampanjer')
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        data-testid="loan-detail-modal"
        className="card max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start mb-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
              {row.MerkeRaw} {fmtText(row.Modell)}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {fmtText(row.Variant)} · {fmtText(row.FinansieringsselskapRaw)}
            </p>
          </div>
          <button onClick={onClose} aria-label={t('loan.modal.close')} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">{t('loan.modal.kampanje')}</h3>
            <DetailRow label={t('loan.modal.kampanjenavn')} value={fmtText(row.Kampanjenavn)} />
            <DetailRow label={t('loan.modal.status')} value={fmtText(row.Status)} />
            <DetailRow label={t('loan.modal.bank')} value={fmtText(row.BankCode)} />
            <DetailRow label={t('loan.modal.kampanjeFra')} value={fmtDato(row.KampanjeFra)} />
            <DetailRow label={t('loan.modal.kampanjeTil')} value={fmtDato(row.KampanjeTil)} />
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">{t('loan.modal.betingelser')}</h3>
            <DetailRow label={t('loan.modal.bilpris')} value={fmtKr(row.BilprisNOK)} />
            <DetailRow label={t('loan.modal.nominellRente')} value={fmtPct(row.NominellRentePct)} />
            {/* Derived by the backend from Merknad — "–" when the note names
                neither flytende nor fast, which is half the batch. */}
            <DetailRow label={t('loan.modal.lanetypeP1')} value={fmtText(row.lanetype_p1)} />
            <DetailRow label={t('loan.modal.effektivRente')} value={fmtPct(row.EffektivRentePct)} />
            <DetailRow label={t('loan.modal.kampanjeperiode')} value={fmtMnd(row.RentebindingMnd)} />
            <DetailRow label={t('loan.modal.renteEtterKampanje')} value={fmtPct(row.ProduktrenteEtterKampanjePct)} />
            <DetailRow label={t('loan.modal.egenkapital')} value={fmtKr(row.EgenkapitalNOK)} />
            <DetailRow label={t('loan.modal.egenkapitalPct')} value={fmtPct(row.EgenkapitalPct)} />
            <DetailRow label={t('loan.modal.lanebelop')} value={fmtKr(row.LanebelopNOK)} />
            <DetailRow label={t('loan.modal.lopetid')} value={fmtMnd(row.LopetidMnd)} />
            <DetailRow label={t('loan.modal.manedsbelop')} value={fmtKr(row.ManedsbelopNOK)} />
            <DetailRow label={t('loan.modal.totalkostnad')} value={fmtKr(row.TotalkostnadNOK)} />
            <DetailRow label={t('loan.modal.etableringsgebyr')} value={fmtKr(row.EtableringsgebyrNOK)} />
            <DetailRow label={t('loan.modal.termingebyr')} value={fmtKr(row.TermingebyrNOK)} />
          </div>

          {row.InkludertUtstyr && (
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">{t('loan.modal.inkludertUtstyr')}</h3>
              <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line">{row.InkludertUtstyr}</p>
            </div>
          )}

          {/* Merknad carries the monthly payment after the campaign period and
              the total credit cost. Neither exists as a column, so it is shown
              in full rather than truncated. */}
          {row.Merknad && (
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">{t('loan.modal.merknad')}</h3>
              <p data-testid="loan-detail-merknad" className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line">
                {row.Merknad}
              </p>
            </div>
          )}

          {row.EndringFraForrigeKontroll && (
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">{t('loan.modal.endring')}</h3>
              <p data-testid="loan-detail-endring" className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line">
                {row.EndringFraForrigeKontroll}
              </p>
            </div>
          )}

          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">{t('loan.modal.kilde')}</h3>
            <DetailRow label={t('loan.modal.sistKontrollert')} value={fmtDato(row.KildeSistKontrollert)} />
            <div className="flex flex-col gap-1 mt-2">
              {row.KildeUrl && (
                <a
                  href={row.KildeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary-600 dark:text-primary-400 hover:underline inline-flex items-center gap-1"
                >
                  {t('loan.modal.kildeside')} <ExternalLink className="w-3 h-3" />
                </a>
              )}
              {row.KampanjeUrl && row.KampanjeUrl !== row.KildeUrl && (
                <a
                  href={row.KampanjeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary-600 dark:text-primary-400 hover:underline inline-flex items-center gap-1"
                >
                  {t('loan.modal.kampanjeside')} <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
